import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  getSession,
  signOut as svcSignOut,
  startStaffSignIn,
  type AuthUser,
  type Session,
} from '../lib/auth';

interface AuthContextValue {
  user: AuthUser | null;
  /** True while the initial session check (`/api/auth/me`) is in flight. */
  loading: boolean;
  /** Begin staff SSO — full-page redirect to the Worker's OAuth broker. */
  signInWithStaffSSO: () => void;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  // Hydrate from the Worker's staff-SSO cookie via /api/auth/me. Async, so we
  // render a loading state until it resolves rather than flashing the login.
  useEffect(() => {
    let alive = true;
    getSession()
      .then((s) => alive && setSession(s))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: session?.user ?? null,
      loading,
      signInWithStaffSSO: startStaffSignIn,
      signOut: () => {
        void svcSignOut(); // fire-and-forget the server logout; clear the UI now
        setSession(null);
      },
    }),
    [session, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used within <AuthProvider>');
  return value;
}
